/**
 * Minimal inline icon set.
 *
 * Exists because design addon §4.2 forbids communicating state through colour
 * alone — an error alert needs a shape a colour-blind reader can distinguish.
 * Inline SVG rather than an icon dependency: six glyphs do not justify one.
 *
 * Icons are decorative by default (`aria-hidden`) because the components that
 * use them always carry a text label. Pass a `title` only when the icon is the
 * sole carrier of meaning.
 */

export interface IconProps {
  /** Accessible name. Omit when adjacent text already conveys the meaning. */
  readonly title?: string | undefined;
  /**
   * Explicitly allows `undefined` because `exactOptionalPropertyTypes` is on and
   * CSS-module lookups are typed `string | undefined` under
   * `noUncheckedIndexedAccess`. Both flags stay; the prop absorbs the union.
   */
  readonly className?: string | undefined;
}

function iconAttributes({ title, className }: IconProps) {
  return {
    className,
    width: 'var(--size-icon-md)',
    height: 'var(--size-icon-md)',
    viewBox: '0 0 20 20',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': title ? undefined : true,
    role: title ? 'img' : undefined,
    focusable: false,
  };
}

export function InfoIcon(props: IconProps) {
  return (
    <svg {...iconAttributes(props)}>
      {props.title ? <title>{props.title}</title> : null}
      <circle cx="10" cy="10" r="7.25" />
      <path d="M10 9v4.5" />
      <path d="M10 6.5h.01" />
    </svg>
  );
}

export function SuccessIcon(props: IconProps) {
  return (
    <svg {...iconAttributes(props)}>
      {props.title ? <title>{props.title}</title> : null}
      <circle cx="10" cy="10" r="7.25" />
      <path d="M6.75 10.25l2.25 2.25 4.25-4.75" />
    </svg>
  );
}

export function WarningIcon(props: IconProps) {
  return (
    <svg {...iconAttributes(props)}>
      {props.title ? <title>{props.title}</title> : null}
      <path d="M10 2.75L18 16.5H2L10 2.75z" />
      <path d="M10 7.75v3.75" />
      <path d="M10 13.75h.01" />
    </svg>
  );
}

export function ErrorIcon(props: IconProps) {
  return (
    <svg {...iconAttributes(props)}>
      {props.title ? <title>{props.title}</title> : null}
      <circle cx="10" cy="10" r="7.25" />
      <path d="M12.25 7.75l-4.5 4.5" />
      <path d="M7.75 7.75l4.5 4.5" />
    </svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <svg {...iconAttributes(props)}>
      {props.title ? <title>{props.title}</title> : null}
      <path d="M14.5 5.5l-9 9" />
      <path d="M5.5 5.5l9 9" />
    </svg>
  );
}

/** Indeterminate progress ring. The caller owns the spin animation. */
export function SpinnerIcon(props: IconProps) {
  return (
    <svg {...iconAttributes(props)}>
      {props.title ? <title>{props.title}</title> : null}
      <path d="M10 2.75a7.25 7.25 0 105.13 2.12" opacity="0.9" />
    </svg>
  );
}

export function SunIcon(props: IconProps) {
  return (
    <svg {...iconAttributes(props)}>
      {props.title ? <title>{props.title}</title> : null}
      <circle cx="10" cy="10" r="3.5" />
      <path d="M10 2v1.75" />
      <path d="M10 16.25V18" />
      <path d="M2 10h1.75" />
      <path d="M16.25 10H18" />
      <path d="M4.34 4.34l1.24 1.24" />
      <path d="M14.42 14.42l1.24 1.24" />
      <path d="M15.66 4.34l-1.24 1.24" />
      <path d="M5.58 14.42l-1.24 1.24" />
    </svg>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <svg {...iconAttributes(props)}>
      {props.title ? <title>{props.title}</title> : null}
      {/* A crescent cut from a disc, so the shape reads at 20px without fill. */}
      <path d="M15.5 11.4A6.2 6.2 0 0 1 8.6 4.5a6.25 6.25 0 1 0 6.9 6.9z" />
    </svg>
  );
}
